import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, InputAdornment, Chip, LinearProgress,
  Grid, Card, CardContent, Alert, Tooltip, IconButton, Button, ToggleButton,
  ToggleButtonGroup, Snackbar, Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageSearchBar from '../components/PageSearchBar';

// ─── Mfg Swap Table Page ──────────────────────────────────────────────────────
//
// Admin UI for the Sage→D-Tools manufacturer translation table.
// Shows all distinct Sage manufacturer values (truncated, max ~10 chars)
// alongside the canonical D-Tools name. Inline-editable.
//
// Bill Hipp request 2026-04-28.

const MfgSwap = () => {
  const navigate = useNavigate();
  const [rows, setRows]               = useState([]);
  const [dtoolsOptions, setDtoolsOpts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filter, setFilter]           = useState('all'); // all | mapped | unmapped
  const [search, setSearch]           = useState('');
  const [sortBy, setSortBy]           = useState('item_count');
  const [sortDir, setSortDir]         = useState('desc');
  const [edits, setEdits]             = useState({}); // { sage_mfg: dtools_mfg }
  const [saving, setSaving]           = useState(null);
  const [reseeding, setReseeding]     = useState(false);
  const [snack, setSnack]             = useState(null);
  const [error, setError]             = useState(null);

  const token = localStorage.getItem('token');

  const fetchRows = useCallback(() => {
    setLoading(true);
    axios.get('/api/warehouse/mfg-swap', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setRows(r.data?.rows || []);
        setError(null);
      })
      .catch((e) => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    fetchRows();
    axios.get('/api/warehouse/mfg-swap/dtools-options', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setDtoolsOpts((r.data || []).map(x => x.name)))
      .catch(() => {});
  }, [fetchRows, token]);

  // Filtering + sorting
  const visibleRows = useMemo(() => {
    let out = rows;
    if (filter === 'mapped')   out = out.filter(r => r.dtools_mfg);
    if (filter === 'unmapped') out = out.filter(r => !r.dtools_mfg);
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(r =>
        (r.sage_mfg   || '').toLowerCase().includes(q) ||
        (r.dtools_mfg || '').toLowerCase().includes(q) ||
        (r.notes      || '').toLowerCase().includes(q)
      );
    }
    out = [...out].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string'
        ? String(av).localeCompare(String(bv))
        : parseFloat(av) - parseFloat(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, filter, search, sortBy, sortDir]);

  const stats = useMemo(() => {
    const mapped     = rows.filter(r => r.dtools_mfg).length;
    const totalItems = rows.reduce((s, r) => s + (parseInt(r.item_count) || 0), 0);
    const mappedItems = rows.filter(r => r.dtools_mfg).reduce((s, r) => s + (parseInt(r.item_count) || 0), 0);
    return {
      total: rows.length,
      mapped,
      unmapped: rows.length - mapped,
      coverage: rows.length ? Math.round((mapped / rows.length) * 100) : 0,
      itemCoverage: totalItems ? Math.round((mappedItems / totalItems) * 100) : 0,
      totalItems,
      mappedItems,
    };
  }, [rows]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'sage_mfg' ? 'asc' : 'desc'); }
  };

  const handleEdit = (sageMfg, value) => {
    setEdits(e => ({ ...e, [sageMfg]: value }));
  };

  const handleSave = async (row) => {
    const newVal = edits[row.sage_mfg];
    setSaving(row.sage_mfg);
    try {
      const r = await axios.put(
        `/api/warehouse/mfg-swap/${encodeURIComponent(row.sage_mfg)}`,
        { dtools_mfg: newVal || null, notes: row.notes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Replace row in state
      setRows(rs => rs.map(x => x.sage_mfg === row.sage_mfg ? { ...x, ...r.data, is_manual: true } : x));
      setEdits(e => { const c = { ...e }; delete c[row.sage_mfg]; return c; });
      setSnack({ severity: 'success', msg: `Saved ${row.sage_mfg} → ${newVal || '(cleared)'}` });
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.error || e.message });
    } finally {
      setSaving(null);
    }
  };

  const handleClear = async (row) => {
    setSaving(row.sage_mfg);
    try {
      await axios.delete(
        `/api/warehouse/mfg-swap/${encodeURIComponent(row.sage_mfg)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRows(rs => rs.map(x => x.sage_mfg === row.sage_mfg ? { ...x, dtools_mfg: null, notes: null, is_manual: false, updated_by: null } : x));
      setEdits(e => { const c = { ...e }; delete c[row.sage_mfg]; return c; });
      setSnack({ severity: 'info', msg: `Cleared mapping for ${row.sage_mfg} (will be re-suggested on next reseed)` });
    } catch (e) {
      setSnack({ severity: 'error', msg: e.response?.data?.error || e.message });
    } finally {
      setSaving(null);
    }
  };

  const handleReseed = async () => {
    setReseeding(true);
    try {
      const r = await axios.post('/api/warehouse/mfg-swap/reseed', {}, { headers: { Authorization: `Bearer ${token}` } });
      setSnack({ severity: 'success', msg: 'Reseed complete — table refreshed from Sage + D-Tools.' });
      fetchRows();
      // Reload last log line for context
      const lastLines = (r.data?.log || '').split('\n').filter(Boolean).slice(-5).join(' | ');
      console.log('[mfg-swap reseed]', lastLines);
    } catch (e) {
      setSnack({ severity: 'error', msg: 'Reseed failed: ' + (e.response?.data?.detail || e.message) });
    } finally {
      setReseeding(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/warehouse')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>
            Manufacturer Swap Table
          </Typography>
          <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>
            Translate truncated Sage manufacturer names → canonical D-Tools manufacturer names
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReseed}
          disabled={reseeding}
        >
          {reseeding ? 'Reseeding…' : 'Reseed from Sage'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">Total Sage Manufacturers</Typography>
              <Typography variant="h4">{stats.total.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">covering {stats.totalItems.toLocaleString()} items</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderColor: '#2E7D32' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Mapped to D-Tools</Typography>
              <Typography variant="h4" color="#2E7D32">{stats.mapped.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">{stats.coverage}% of mfg names</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderColor: '#E65100' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Unmapped</Typography>
              <Typography variant="h4" color="#E65100">{stats.unmapped.toLocaleString()}</Typography>
              <Typography variant="caption" color="text.secondary">need manual review</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined" sx={{ borderColor: '#1A5490' }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">Item Coverage</Typography>
              <Typography variant="h4" color="#1A5490">{stats.itemCoverage}%</Typography>
              <Typography variant="caption" color="text.secondary">{stats.mappedItems.toLocaleString()} / {stats.totalItems.toLocaleString()} items mapped</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <PageSearchBar
            placeholder="Search Sage or D-Tools name…"
            value={search}
            onChange={(v) => setSearch(v)}
          />
          <ToggleButtonGroup
            size="small"
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
          >
            <ToggleButton value="all">All ({stats.total})</ToggleButton>
            <ToggleButton value="mapped">Mapped ({stats.mapped})</ToggleButton>
            <ToggleButton value="unmapped">Unmapped ({stats.unmapped})</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Showing {visibleRows.length} rows
          </Typography>
        </Box>

        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                {[
                  { key: 'sage_mfg',   label: 'Sage Truncated Name', width: 180 },
                  { key: 'item_count', label: 'Items', align: 'right', width: 100 },
                  { key: 'dtools_mfg', label: 'D-Tools Manufacturer Name (editable)' },
                  { key: 'is_manual',  label: 'Source', width: 110 },
                  { key: 'updated_at', label: 'Updated', width: 160 },
                  { key: '_actions',   label: '', width: 120 },
                ].map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'} sx={{ fontWeight: 'bold', width: col.width }}>
                    {col.key === '_actions' ? '' : (
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortDir : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No rows match.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {visibleRows.map((row) => {
                const editing      = Object.prototype.hasOwnProperty.call(edits, row.sage_mfg);
                const editValue    = editing ? edits[row.sage_mfg] : (row.dtools_mfg || '');
                const dirty        = editing && (editValue || '') !== (row.dtools_mfg || '');
                const isSaving     = saving === row.sage_mfg;
                return (
                  <TableRow key={row.sage_mfg} hover sx={{ bgcolor: dirty ? '#fff8e1' : undefined }}>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant="body2" fontFamily="monospace" fontWeight="bold" color="primary.main">
                        {row.sage_mfg}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>
                      <Typography variant="body2">{row.item_count?.toLocaleString() || 0}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Autocomplete
                        size="small"
                        freeSolo
                        options={dtoolsOptions}
                        value={editValue}
                        onChange={(_, v) => handleEdit(row.sage_mfg, v || '')}
                        onInputChange={(_, v) => handleEdit(row.sage_mfg, v || '')}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="(no mapping — falls back to Sage value)"
                            variant="standard"
                            sx={{ minWidth: 320 }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {row.is_manual ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Manual"
                          size="small"
                          color="primary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : row.dtools_mfg ? (
                        <Chip
                          icon={<SwapHorizIcon />}
                          label="Auto"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Chip
                          icon={<WarningIcon />}
                          label="Unmapped"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}
                        {row.updated_by_name ? ` by ${row.updated_by_name}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }} align="right">
                      <Tooltip title={dirty ? 'Save change' : 'No changes'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={!dirty || isSaving}
                            onClick={() => handleSave(row)}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Clear (revert to auto-suggest on next reseed)">
                        <span>
                          <IconButton
                            size="small"
                            disabled={!row.dtools_mfg || isSaving}
                            onClick={() => handleClear(row)}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snack ? <Alert severity={snack.severity} onClose={() => setSnack(null)}>{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default MfgSwap;
